import {
  Button,
  confirm,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  Modal,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  BarChart,
  Calendar,
  GripVertical,
  Lightbulb,
  MinusIcon,
  PlusIcon,
  Save,
  Settings,
  Sparkles,
} from "lucide-react";
import { forwardRef, useEffect } from "react";
import { z } from "zod";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { JDDetailDTO } from "@/modules/jd/domain/contracts";
import type { JDLevelDTO } from "@/modules/jd/domain/contracts/level";
import {
  useCreateJDLevel,
  useDeleteJDLevel,
  useJDLevels,
  useUpdateJDLevel,
} from "@/modules/jd/presentation/api/queries";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { config } from "@/shared/lib/config";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormTipTapEditor } from "@/shared/ui/FormTipTapEditor";
import { JDLevelForm } from "./JDLevelForm";

type DepartmentItem = LookupItem;
type PositionItem = LookupItem & {
  title?: string;
  departmentId?: string;
  level?: string;
};

const JDFormSchema = z.object({
  title: z.string().optional(),
  positionId: z.string().optional(),
  departmentId: z.string().optional(),
  level: z.string().optional(), // levelId from combobox
  reportsToPositionId: z.string().optional(),
  managesCount: z.string().optional(),
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  responsibilities: z
    .array(
      z.object({
        description: z.string().optional(),
        orderIndex: z.number().default(0),
      }),
    )
    .default([]),
  routineTasks: z
    .array(
      z.object({
        description: z.string().optional(),
        frequency: z.enum([
          "daily",
          "weekly",
          "monthly",
          "quarterly",
          "annual",
        ]),
        orderIndex: z.number().default(0),
      }),
    )
    .default([]),
  requirements: z
    .array(
      z.object({
        type: z.enum([
          "experience",
          "skills",
          "knowledge",
          "education",
          "other",
        ]),
        description: z.string().optional(),
        isRequired: z.union([z.boolean(), z.string()]).transform((val) => {
          if (val === "true" || val === true) return true;
          if (val === "false" || val === false) return false;
          return false; // default
        }),
        orderIndex: z.number().default(0),
      }),
    )
    .default([]),
});

export type JDFormValues = z.infer<typeof JDFormSchema>;

export type JDFormRef = {
  fillTestData: () => void;
};

// Sortable Responsibility Item Component
function SortableResponsibilityItem({
  id,
  index,
  onRemove,
}: {
  id: string;
  index: number;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-2">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="mt-2 cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-5 w-5" />
      </button>
      <FormTipTapEditor
        name={`responsibilities.${index}.description`}
        placeholder="ຕົວຢ່າງ: ດູແລຍອດຂາຍປະຈຳເດືອນ, ຈັດການທີມງານຂາຍ, ວາງຜະແນກຍຸດທະສາດການຕະຫຼາດ"
        className="flex-1"
        toolbarElements={["bold", "italic", "bulletList", "orderedList"]}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        className="mt-2"
      >
        <MinusIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}

// Level Management Component
function LevelManagementButton() {
  const canManage = useActionPermission([
    "jd:create",
    "jd:update",
    "jd:delete",
  ]);
  const listModal = useDisclosure();
  const createModal = useDisclosure();
  const editModal = useDisclosure<JDLevelDTO>();
  const listQuery = useJDLevels({ limit: 100, offset: 0 });
  const createLevel = useCreateJDLevel();
  const updateLevel = useUpdateJDLevel();
  const deleteLevel = useDeleteJDLevel();

  if (!canManage) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={() => listModal.open()}
        title="ຈັດການລະດັບ"
        className="mt-6"
      >
        <Settings className="h-4 w-4" />
      </Button>

      {/* Levels List Modal */}
      <Modal
        open={!!listModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            listModal.close();
            createModal.close();
            editModal.close();
          }
        }}
        title="ຈັດການລະດັບ"
        description="ສ້າງ, ແກ້ໄຂ, ຫຼື ລຶບລະດັບຕໍາແໜ່ງ"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                listModal.close();
                createModal.open();
              }}
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              ສ້າງລະດັບໃໝ່
            </Button>
          </div>

          {listQuery.isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              ກຳລັງໂຫຼດ...
            </div>
          ) : listQuery.data?.data.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              ຍັງບໍ່ມີລະດັບ
            </div>
          ) : (
            <div className="space-y-2">
              {listQuery.data?.data.map((level) => (
                <div
                  key={level.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{level.name}</span>
                    <span className="text-muted-foreground text-xs">
                      ລໍາດັບ: {level.orderIndex}
                    </span>
                    {!level.isActive && (
                      <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground text-xs">
                        ບໍ່ໃຊ້ງານ
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        listModal.close();
                        editModal.openWith(level);
                      }}
                    >
                      ແກ້ໄຂ
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        if (
                          await confirm({
                            title: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບລະດັບ "${level.name}"?`,
                            description: "ລະດັບຕໍາແໜ່ງນີ້ຈະລຶບອอกຈາກລະດັບຕໍາແໜ່ງທັງຫມົດ",
                            actionText: "ລຶບ",
                            ActionProps: {
                              variant: "destructive",
                            },
                          })
                        ) {
                          await deleteLevel.mutateAsync(level.id);
                        }
                      }}
                    >
                      ລຶບ
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Create Level Modal */}
      <Modal
        open={!!createModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            createModal.close();
          } else {
            listModal.close();
          }
        }}
        title="ສ້າງລະດັບ"
        description="ກໍານົດຊື່, ລໍາດັບ, ແລະສະຖານະຂອງລະດັບ."
        size="sm"
      >
        <JDLevelForm
          onSubmit={async (vals) => {
            await createLevel.mutateAsync({
              name: vals.name || "",
              orderIndex: vals.orderIndex ?? 0,
              isActive: vals.isActive ?? true,
            });
            createModal.close();
            listModal.open();
          }}
          submitting={createLevel.isPending}
        />
      </Modal>

      {/* Edit Level Modal */}
      <Modal
        open={!!editModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            editModal.close();
          } else {
            listModal.close();
          }
        }}
        title="ແກ້ໄຂລະດັບ"
        description="ປັບປຸງລາຍລະອຽດຂອງລະດັບ."
        size="sm"
      >
        <JDLevelForm
          initialValues={
            editModal.data
              ? {
                  name: editModal.data.name,
                  orderIndex: editModal.data.orderIndex,
                  isActive: editModal.data.isActive,
                }
              : undefined
          }
          onSubmit={async (vals) => {
            if (!editModal.data) return;
            await updateLevel.mutateAsync({
              id: editModal.data.id,
              input: {
                name: vals.name || "",
                orderIndex: vals.orderIndex ?? 0,
                isActive: vals.isActive ?? true,
              },
            });
            editModal.close();
            listModal.open();
          }}
          submitting={updateLevel.isPending}
        />
      </Modal>
    </>
  );
}

export const JDForm = forwardRef<
  JDFormRef,
  {
    initialValues?: Partial<JDDetailDTO>;
    onSubmit: (values: {
      title: string;
      departmentId: string;
      level: string;
      reportsToPositionId?: string | null;
      managesCount?: number | null;
      status: string;
      responsibilities: Array<{ description: string; orderIndex: number }>;
      routineTasks: Array<{
        description: string;
        frequency: string;
        orderIndex: number;
      }>;
      requirements: Array<{
        type: string;
        description: string;
        isRequired: boolean;
        orderIndex: number;
      }>;
    }) => void;
    submitting?: boolean;
  }
>(({ initialValues, onSubmit, submitting }, _ref) => {
  const methods = RHF.useForm<JDFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference issue with optional fields
    resolver: zodResolver(JDFormSchema) as any,
    defaultValues: {
      title: initialValues?.title || "",
      positionId: "",
      departmentId: initialValues?.departmentId || "",
      level: initialValues?.levelId || "",
      reportsToPositionId: initialValues?.reportsToPositionId || "",
      managesCount: initialValues?.managesCount?.toString() || "",
      status: (initialValues?.status || "draft") as JDFormValues["status"],
      responsibilities:
        initialValues?.responsibilities?.map((r) => ({
          description: r.description,
          orderIndex: r.orderIndex,
        })) || [],
      routineTasks:
        initialValues?.routineTasks?.map((t) => ({
          description: t.description,
          frequency: t.frequency,
          orderIndex: t.orderIndex,
        })) || [],
      requirements:
        initialValues?.requirements?.map((req) => ({
          type: req.type,
          description: req.description,
          // biome-ignore lint/suspicious/noExplicitAny: FormSelect uses strings, schema transforms to boolean
          isRequired: String(req.isRequired ?? false) as any,
          orderIndex: req.orderIndex,
        })) || [],
    },
  });

  const responsibilities = RHF.useFieldArray({
    control: methods.control,
    name: "responsibilities",
  });

  const routineTasks = RHF.useFieldArray({
    control: methods.control,
    name: "routineTasks",
  });

  const requirements = RHF.useFieldArray({
    control: methods.control,
    name: "requirements",
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle drag end for responsibilities
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = responsibilities.fields.findIndex(
        (f) => f.id === active.id,
      );
      const newIndex = responsibilities.fields.findIndex(
        (f) => f.id === over.id,
      );
      responsibilities.move(oldIndex, newIndex);
      // Update orderIndex values
      const newFields = arrayMove(responsibilities.fields, oldIndex, newIndex);
      newFields.forEach((_, idx) => {
        methods.setValue(`responsibilities.${idx}.orderIndex`, idx);
      });
    }
  };

  // Auto-save draft to localStorage
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const subscription = methods.watch(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const draftKey = initialValues?.id
          ? `jd-draft-${initialValues.id}`
          : "jd-draft-new";
        try {
          const formValues = methods.getValues();
          localStorage.setItem(draftKey, JSON.stringify(formValues));
        } catch (error) {
          console.warn("Failed to save draft:", error);
        }
      }, 2000); // Debounce 2 seconds
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [methods, initialValues?.id]);

  // Load draft from localStorage on mount
  useEffect(() => {
    if (!initialValues?.id) {
      const draftKey = "jd-draft-new";
      try {
        const draft = localStorage.getItem(draftKey);
        if (draft) {
          const parsed = JSON.parse(draft);
          Object.keys(parsed).forEach((key) => {
            if (parsed[key] !== undefined && parsed[key] !== null) {
              methods.setValue(key as keyof JDFormValues, parsed[key]);
            }
          });
        }
      } catch (error) {
        console.warn("Failed to load draft:", error);
      }
    }
  }, [initialValues?.id, methods]);

  // Watch positionId to auto-fill when position is selected
  const positionId = methods.watch("positionId");
  useEffect(() => {
    if (positionId) {
      // Fetch position details and auto-fill
      hydrateLookupItem<PositionItem>(
        `${config.apiUrl}/positions/lookup`,
        positionId,
      ).then((position) => {
        if (position) {
          const title = (position as PositionItem).title || position.name;
          methods.setValue("title", title);
          if ((position as PositionItem).departmentId) {
            methods.setValue(
              "departmentId",
              (position as PositionItem).departmentId ?? "",
            );
          }
          if ((position as PositionItem).level) {
            methods.setValue(
              "level",
              (position as PositionItem).level as JDFormValues["level"],
            );
          }
        }
      });
    }
  }, [positionId, methods]);

  // Check if in dev mode
  const isDevMode =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  // Generate random data for auto fill
  const generateRandomData = () => {
    const titles = [
      "ຜູ້ຈັດການຂາຍ",
      "ນັກບັນຊີ",
      "ຜູ້ຈັດການຊັບພະຍາກອນມະນຸດ",
      "ຜູ້ອອກແບບຜະລິດຕະພັນ",
      "ຜູ້ຈັດການການຕະຫຼາດ",
      "ນັກພັດທະນາໂປຣແກຣມ",
      "ຜູ້ຈັດການຄຸນນະພາບ",
      "ຜູ້ຊ່ວຍຜູ້ຈັດການ",
    ];
    const levels: Array<
      "Junior" | "Senior" | "Manager" | "Director" | "Other"
    > = ["Junior", "Senior", "Manager", "Director", "Other"];
    const frequencies: Array<
      "daily" | "weekly" | "monthly" | "quarterly" | "annual"
    > = ["daily", "weekly", "monthly", "quarterly", "annual"];
    const requirementTypes: Array<
      "experience" | "skills" | "knowledge" | "education" | "other"
    > = ["experience", "skills", "knowledge", "education", "other"];

    const randomTitle = titles[Math.floor(Math.random() * titles.length)];
    const randomLevel = levels[Math.floor(Math.random() * levels.length)];
    const randomManagesCount = Math.floor(Math.random() * 10);

    // Generate random responsibilities
    const responsibilityTemplates = [
      "ດູແລຍອດຂາຍປະຈຳເດືອນ",
      "ຈັດການທີມງານ",
      "ວາງຜະແນກຍຸດທະສາດ",
      "ຕິດຕາມລູກຄ້າ",
      "ສ້າງຄວາມສຳພັນ",
      "ຈັດການຂໍ້ມູນ",
      "ລາຍງານຜົນການເຮັດວຽກ",
      "ປະສານງານກັບພະແນກອື່ນ",
    ];
    const responsibilities = Array.from(
      { length: Math.floor(Math.random() * 5) + 3 },
      (_, i) => ({
        description:
          responsibilityTemplates[
            Math.floor(Math.random() * responsibilityTemplates.length)
          ] + ` ${i + 1}`,
        orderIndex: i,
      }),
    );

    // Generate random routine tasks
    const taskTemplates = [
      "ກວດສອບອີເມວ",
      "ຕິດຕາມຄຳສັ່ງຊື້",
      "ສົນທະນາກັບທີມງານ",
      "ລາຍງານຜົນການເຮັດວຽກ",
      "ວາງພະແນການເຮັດວຽກ",
      "ປະຊຸມທີມງານ",
      "ກວດສອບຄຸນນະພາບ",
      "ຕິດຕາມຄວາມຄືບໜ້າ",
    ];
    const routineTasks = Array.from(
      { length: Math.floor(Math.random() * 6) + 4 },
      (_, i) => ({
        description:
          taskTemplates[Math.floor(Math.random() * taskTemplates.length)] +
          ` ${i + 1}`,
        frequency: frequencies[
          Math.floor(Math.random() * frequencies.length)
        ] as "daily" | "weekly" | "monthly" | "quarterly" | "annual",
        orderIndex: i,
      }),
    );

    // Generate random requirements
    const requirementTemplates = [
      "ມີປະສົບການຢ່າງໜ້ອຍ 3 ປີ",
      "ຮູ້ໃຊ້ Microsoft Office",
      "ຄວາມຮູ້ດ້ານການຕະຫຼາດ",
      "ຈົບປະລິນຍາຕີ",
      "ທັກສະການສື່ສານ",
      "ຄວາມຮູ້ດ້ານ CRM",
      "ທັກສະການຈັດການ",
      "ຄວາມຮູ້ດ້ານການຂາຍ",
    ];
    const requirements = Array.from(
      { length: Math.floor(Math.random() * 5) + 3 },
      (_, i) => ({
        type: requirementTypes[
          Math.floor(Math.random() * requirementTypes.length)
        ] as "experience" | "skills" | "knowledge" | "education" | "other",
        description:
          requirementTemplates[
            Math.floor(Math.random() * requirementTemplates.length)
          ],
        isRequired: Math.random() > 0.3,
        orderIndex: i,
      }),
    );

    // Fill form with random data
    methods.setValue("title", randomTitle);
    methods.setValue("level", randomLevel);
    methods.setValue("managesCount", randomManagesCount.toString());
    methods.setValue("status", Math.random() > 0.5 ? "draft" : "published");
    methods.setValue("responsibilities", responsibilities);
    methods.setValue("routineTasks", routineTasks);
    methods.setValue("requirements", requirements);

    toast.success("ຕື່ມຂໍ້ມູນສຸ່ມແລ້ວ");
  };

  return (
    <FormRoot<JDFormValues>
      methods={methods}
      onSubmit={(vals) => {
        // Use title from position selection or manual input
        const finalTitle = vals.title?.trim() || "";
        const finalDepartmentId = vals.departmentId?.trim() || "";
        const finalLevelId = vals.level?.trim() || "";

        // Validate required fields
        if (!finalTitle) {
          methods.setError("title", {
            type: "required",
            message: "ກະລຸນາປ້ອນຊື່ຕໍາແໜ່ງ",
          });
          return;
        }

        if (!finalDepartmentId) {
          methods.setError("departmentId", {
            type: "required",
            message: "ກະລຸນາເລືອກພະແນກ",
          });
          return;
        }

        if (!finalLevelId) {
          methods.setError("level", {
            type: "required",
            message: "ກະລຸນາເລືອກລະດັບ",
          });
          return;
        }

        // Clear draft on successful submit
        const draftKey = initialValues?.id
          ? `jd-draft-${initialValues.id}`
          : "jd-draft-new";
        try {
          localStorage.removeItem(draftKey);
        } catch (error) {
          console.warn("Failed to clear draft:", error);
        }

        onSubmit({
          title: finalTitle,
          departmentId: finalDepartmentId,
          level: finalLevelId, // levelId from combobox
          reportsToPositionId: vals.reportsToPositionId || null,
          managesCount: vals.managesCount
            ? Number.parseInt(vals.managesCount, 10) || null
            : null,
          status: vals.status,
          responsibilities: vals.responsibilities
            .filter((r) => r.description?.trim())
            .map((r, idx) => ({
              description: r.description || "",
              orderIndex: r.orderIndex ?? idx,
            })),
          routineTasks: vals.routineTasks
            .filter((t) => t.description?.trim())
            .map((t, idx) => ({
              description: t.description || "",
              frequency: t.frequency,
              orderIndex: t.orderIndex ?? idx,
            })),
          requirements: vals.requirements
            .filter((req) => req.description?.trim())
            .map((req, idx) => ({
              type: req.type,
              description: req.description || "",
              isRequired: req.isRequired ?? false,
              orderIndex: req.orderIndex ?? idx,
            })),
        });
      }}
      className="space-y-8"
    >
      {/* Step Indicator */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm">
          <span className="font-medium text-foreground">ສ່ວນທີ 1</span>
          <span>/</span>
          <span>ສ່ວນທີ 2</span>
          <span>/</span>
          <span>ສ່ວນທີ 3</span>
          <span>/</span>
          <span>ສ່ວນທີ 4</span>
          <span>/</span>
          <span>ສ່ວນທີ 5</span>
        </div>
        {isDevMode && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={generateRandomData}
            className="text-xs"
          >
            <Sparkles className="mr-1 h-3 w-3" />
            Auto Fill (Dev)
          </Button>
        )}
      </div>

      {/* Section 1: Basic Information */}
      <div className="space-y-4 rounded-lg border p-6">
        <h3 className="font-semibold text-foreground text-lg">
          ສ່ວນທີ 1: ຂໍ້ມູນພື້ນຖານ
        </h3>
        <div className="space-y-4">
          <div>
            <FormInfiniteCombobox<PositionItem>
              name="positionId"
              label="ເລືອກຕໍາແໜ່ງ (ບໍ່ບັງຄັບ)"
              queryKey={["positions", "form"]}
              queryFn={(args) =>
                fetchLookupForInfinite(
                  `${config.apiUrl}/positions/lookup`,
                  args,
                )
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem(`${config.apiUrl}/positions/lookup`, id)
              }
              getLabel={(item) => (item as PositionItem).title || item.name}
              getValue={(item) => item.id}
              placeholder="ເລືອກຕໍາແໜ່ງທີ່ມີຢູ່ແລ້ວ (ຈະຕື່ມຂໍ້ມູນອັດຕະໂນມັດ)"
              clearable
            />
            <p className="mt-1 flex items-center gap-1 text-muted-foreground text-xs">
              <Lightbulb className="h-3 w-3" />
              ເລືອກຕໍາແໜ່ງເພື່ອຕື່ມຂໍ້ມູນອັດຕະໂນມັດ ຫຼື ຂ້າມໄປກະລຸນາປ້ອນດ້ວຍຕົນເອງ
            </p>
          </div>
          <FormInput
            name="title"
            label="ຊື່ຕໍາແໜ່ງ"
            requiredMark
            placeholder="ຕົວຢ່າງ: ຜູ້ຈັດການຂາຍ, ນັກບັນຊີ, ຜູ້ຈັດການຊັບພະຍາກອນມະນຸດ"
          />
          <FormInfiniteCombobox<DepartmentItem>
            name="departmentId"
            label="ເລືອກພະແນກ"
            requiredMark
            queryKey={["departments", "form"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກພະແນກທີ່ຕໍາແໜ່ງນີ້ຢູ່"
          />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FormInfiniteCombobox<LookupItem>
                queryKey={["jd-levels", "form"]}
                name="level"
                label="ເລືອກລະດັບຕໍາແໜ່ງ"
                requiredMark
                queryFn={(args) =>
                  fetchLookupForInfinite(`${config.apiUrl}/jd/levels/lookup`, {
                    search: args.search,
                    pageParam: args.pageParam,
                  })
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem(`${config.apiUrl}/jd/levels/lookup`, id)
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                placeholder="ເລືອກລະດັບຕໍາແໜ່ງ"
                className="flex-1"
              />
              <LevelManagementButton />
            </div>
          </div>
          <FormInfiniteCombobox<PositionItem>
            name="reportsToPositionId"
            label="ເລືອກຫົວໜ້າທີ່ລາຍງານ"
            queryKey={["positions", "reports-to"]}
            queryFn={(args) =>
              fetchLookupForInfinite(`${config.apiUrl}/positions/lookup`, args)
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/positions/lookup`, id)
            }
            getLabel={(item) => (item as PositionItem).title || item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກຕໍາແໜ່ງທີ່ຕ້ອງລາຍງານ (ຖ້າມີ)"
            clearable
          />
          <FormInput
            name="managesCount"
            label="ຈຳນວນຄົນທີ່ດູແລ"
            type="number"
            placeholder="ຕົວຢ່າງ: 5 (ຖ້າຕໍາແໜ່ງນີ້ມີທີມງານ)"
          />
        </div>
      </div>

      {/* Section 2: Job Responsibilities */}
      <div className="space-y-4 rounded-lg border p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground text-lg">
            ສ່ວນທີ 2: ຫນ້າທີ່ຫຼັກ
          </h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              responsibilities.append({
                description: "",
                orderIndex: responsibilities.fields.length,
              })
            }
          >
            <PlusIcon className="mr-1 h-4 w-4" />
            ເພີ່ມຫນ້າທີ່
          </Button>
        </div>
        <div className="space-y-3">
          {responsibilities.fields.length === 0 ? (
            <p className="text-muted-foreground text-sm italic">
              ຍັງບໍ່ມີຫນ້າທີ່. ກົດປຸ່ມ "ເພີ່ມຫນ້າທີ່" ເພື່ອເລີ່ມຕົ້ນ
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={responsibilities.fields.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                {responsibilities.fields.map((field, index) => (
                  <SortableResponsibilityItem
                    key={field.id}
                    id={field.id}
                    index={index}
                    onRemove={() => responsibilities.remove(index)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
        <p className="flex items-center gap-1 text-muted-foreground text-xs">
          <Lightbulb className="h-3 w-3" />
          ສາມາດລາກເພື່ອຈັດລຳດັບຫນ້າທີ່ໄດ້
        </p>
      </div>

      {/* Section 3: Routine Tasks - Organized by Frequency */}
      <div className="space-y-4 rounded-lg border p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground text-lg">
            ສ່ວນທີ 3: ວຽກປະຈຳ
          </h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              routineTasks.append({
                description: "",
                frequency: "daily",
                orderIndex: routineTasks.fields.length,
              })
            }
          >
            <PlusIcon className="mr-1 h-4 w-4" />
            ເພີ່ມວຽກ
          </Button>
        </div>

        {/* Daily Tasks */}
        <div className="space-y-3">
          <h4 className="flex items-center gap-1 font-medium text-foreground/80 text-sm">
            <Calendar className="h-4 w-4" />
            ວຽກປະຈຳວັນ
          </h4>
          {routineTasks.fields.filter((_, idx) => {
            const freq = methods.watch(`routineTasks.${idx}.frequency`);
            return freq === "daily";
          }).length === 0 ? (
            <p className="py-2 text-muted-foreground text-sm italic">
              ຍັງບໍ່ມີວຽກປະຈຳວັນ. ກົດປຸ່ມ "ເພີ່ມວຽກ" ແລ້ວເລືອກຄວາມຖີ່ "ປະຈຳວັນ"
            </p>
          ) : (
            routineTasks.fields
              .filter((_, idx) => {
                const freq = methods.watch(`routineTasks.${idx}.frequency`);
                return freq === "daily";
              })
              .map((field) => {
                const actualIndex = routineTasks.fields.findIndex(
                  (f) => f.id === field.id,
                );
                return (
                  <div
                    key={field.id}
                    className="space-y-2 rounded-lg border bg-muted/30 p-3"
                  >
                    <div className="flex gap-2">
                      <FormTextarea
                        name={`routineTasks.${actualIndex}.description`}
                        placeholder="ຕົວຢ່າງ: ກວດສອບອີເມວ, ຕິດຕາມຄຳສັ່ງຊື້, ຕິດຕາມລູກຄ້າ"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => routineTasks.remove(actualIndex)}
                      >
                        <MinusIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <FormSelect
                      name={`routineTasks.${actualIndex}.frequency`}
                      label="ຄວາມຖີ່"
                      options={[
                        { value: "daily", label: "ປະຈຳວັນ" },
                        { value: "weekly", label: "ປະຈຳອາທິດ" },
                        { value: "monthly", label: "ປະຈຳເດືອນ" },
                        { value: "quarterly", label: "ປະຈຳໄຕມາດ" },
                        { value: "annual", label: "ປະຈຳປີ" },
                      ]}
                    />
                  </div>
                );
              })
          )}
        </div>

        {/* Weekly/Monthly Tasks */}
        <div className="space-y-3">
          <h4 className="flex items-center gap-1 font-medium text-foreground/80 text-sm">
            <Calendar className="h-4 w-4" />
            ວຽກປະຈຳອາທິດ / ເດືອນ
          </h4>
          {routineTasks.fields.filter((_, idx) => {
            const freq = methods.watch(`routineTasks.${idx}.frequency`);
            return freq === "weekly" || freq === "monthly";
          }).length === 0 ? (
            <p className="py-2 text-muted-foreground text-sm italic">
              ຍັງບໍ່ມີວຽກປະຈຳອາທິດ/ເດືອນ. ກົດປຸ່ມ "ເພີ່ມວຽກ" ແລ້ວເລືອກຄວາມຖີ່ "ປະຈຳອາທິດ" ຫຼື
              "ປະຈຳເດືອນ"
            </p>
          ) : (
            routineTasks.fields
              .filter((_, idx) => {
                const freq = methods.watch(`routineTasks.${idx}.frequency`);
                return freq === "weekly" || freq === "monthly";
              })
              .map((field) => {
                const actualIndex = routineTasks.fields.findIndex(
                  (f) => f.id === field.id,
                );
                return (
                  <div
                    key={field.id}
                    className="space-y-2 rounded-lg border bg-muted/30 p-3"
                  >
                    <div className="flex gap-2">
                      <FormTextarea
                        name={`routineTasks.${actualIndex}.description`}
                        placeholder="ຕົວຢ່າງ: ສົນທະນາກັບທີມງານ, ລາຍງານຜົນການເຮັດວຽກ, ວາງພະແນການເຮັດວຽກ"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => routineTasks.remove(actualIndex)}
                      >
                        <MinusIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <FormSelect
                      name={`routineTasks.${actualIndex}.frequency`}
                      label="ຄວາມຖີ່"
                      options={[
                        { value: "daily", label: "ປະຈຳວັນ" },
                        { value: "weekly", label: "ປະຈຳອາທິດ" },
                        { value: "monthly", label: "ປະຈຳເດືອນ" },
                        { value: "quarterly", label: "ປະຈຳໄຕມາດ" },
                        { value: "annual", label: "ປະຈຳປີ" },
                      ]}
                    />
                  </div>
                );
              })
          )}
        </div>

        {/* Other Frequencies */}
        {routineTasks.fields.some((_, idx) => {
          const freq = methods.watch(`routineTasks.${idx}.frequency`);
          return freq === "quarterly" || freq === "annual";
        }) && (
          <div className="space-y-3">
            <h4 className="flex items-center gap-1 font-medium text-foreground/80 text-sm">
              <BarChart className="h-4 w-4" />
              ວຽກອື່ນໆ
            </h4>
            {routineTasks.fields
              .filter((_, idx) => {
                const freq = methods.watch(`routineTasks.${idx}.frequency`);
                return freq === "quarterly" || freq === "annual";
              })
              .map((field) => {
                const actualIndex = routineTasks.fields.findIndex(
                  (f) => f.id === field.id,
                );
                return (
                  <div
                    key={field.id}
                    className="space-y-2 rounded-lg border bg-muted/30 p-3"
                  >
                    <div className="flex gap-2">
                      <FormTextarea
                        name={`routineTasks.${actualIndex}.description`}
                        placeholder="ລາຍລະອຽດວຽກ"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => routineTasks.remove(actualIndex)}
                      >
                        <MinusIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <FormSelect
                      name={`routineTasks.${actualIndex}.frequency`}
                      label="ຄວາມຖີ່"
                      options={[
                        { value: "daily", label: "ປະຈຳວັນ" },
                        { value: "weekly", label: "ປະຈຳອາທິດ" },
                        { value: "monthly", label: "ປະຈຳເດືອນ" },
                        { value: "quarterly", label: "ປະຈຳໄຕມາດ" },
                        { value: "annual", label: "ປະຈຳປີ" },
                      ]}
                    />
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Section 4: Requirements */}
      <div className="space-y-4 rounded-lg border p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground text-lg">
            ສ່ວນທີ 4: ຄຸນສົມບັດທີ່ຕ້ອງການ
          </h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              requirements.append({
                type: "skills",
                description: "",
                isRequired: false,
                orderIndex: requirements.fields.length,
              })
            }
          >
            <PlusIcon className="mr-1 h-4 w-4" />
            ເພີ່ມຄຸນສົມບັດ
          </Button>
        </div>
        <div className="space-y-3">
          {requirements.fields.length === 0 ? (
            <p className="py-2 text-muted-foreground text-sm italic">
              ຍັງບໍ່ມີຄຸນສົມບັດ. ກົດປຸ່ມ "ເພີ່ມຄຸນສົມບັດ" ເພື່ອເລີ່ມຕົ້ນ
            </p>
          ) : (
            requirements.fields.map((field, index) => (
              <div key={field.id} className="space-y-2 rounded-lg border p-3">
                <div className="flex gap-2">
                  <FormTextarea
                    name={`requirements.${index}.description`}
                    placeholder="ຕົວຢ່າງ: ມີປະສົບການຂາຍ 3 ປີ, ຮູ້ໃຊ້ Microsoft Office, ຈົບປະລິນຍາຕີ"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => requirements.remove(index)}
                  >
                    <MinusIcon className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex gap-2">
                  <FormSelect
                    name={`requirements.${index}.type`}
                    label="ປະເພດ"
                    options={[
                      { value: "experience", label: "ປະສົບການ" },
                      { value: "skills", label: "ທັກສະ" },
                      { value: "knowledge", label: "ຄວາມຮູ້" },
                      { value: "education", label: "ການສຶກສາ" },
                      { value: "other", label: "ອື່ນໆ" },
                    ]}
                  />
                  <div className="w-32">
                    <FormSelect
                      name={`requirements.${index}.isRequired`}
                      label="ຈຳເປັນ"
                      options={[
                        { value: "false", label: "ບໍ່ຈຳເປັນ" },
                        { value: "true", label: "ຈຳເປັນ" },
                      ]}
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <p className="flex items-center gap-1 text-muted-foreground text-xs">
          <Lightbulb className="h-3 w-3" />
          ບອກວ່າຕ້ອງການຄຸນສົມບັດຫຍັງແດ່ ສຳລັບຕໍາແໜ່ງນີ້ (ບໍ່ຈຳເປັນຕ້ອງຍາວ)
        </p>
      </div>

      <div className="sticky bottom-2 flex items-center justify-between border-t bg-background pt-4">
        <Button
          type="button"
          variant="outline"
          isLoading={submitting}
          onClick={async () => {
            const vals = methods.getValues();
            const finalTitle = vals.title?.trim() || "";
            const finalDepartmentId = vals.departmentId?.trim() || "";
            const finalLevelId = vals.level?.trim() || "";

            // Validate required fields
            if (!finalTitle) {
              methods.setError("title", {
                type: "required",
                message: "ກະລຸນາປ້ອນຊື່ຕໍາແໜ່ງ",
              });
              return;
            }

            if (!finalDepartmentId) {
              methods.setError("departmentId", {
                type: "required",
                message: "ກະລຸນາເລືອກພະແນກ",
              });
              return;
            }

            if (!finalLevelId) {
              methods.setError("level", {
                type: "required",
                message: "ກະລຸນາເລືອກລະດັບ",
              });
              return;
            }

            // Clear draft on successful submit
            const draftKey = initialValues?.id
              ? `jd-draft-${initialValues.id}`
              : "jd-draft-new";
            try {
              localStorage.removeItem(draftKey);
            } catch (error) {
              console.warn("Failed to clear draft:", error);
            }

            // Call onSubmit with status = "draft"
            onSubmit({
              title: finalTitle,
              departmentId: finalDepartmentId,
              level: finalLevelId, // levelId from combobox
              reportsToPositionId: vals.reportsToPositionId || null,
              managesCount: vals.managesCount
                ? Number.parseInt(vals.managesCount, 10)
                : null,
              status: "draft", // Always save as draft
              responsibilities: vals.responsibilities.map((r, idx) => ({
                description: r.description || "",
                orderIndex: idx,
              })),
              routineTasks: vals.routineTasks.map((t, idx) => ({
                description: t.description || "",
                frequency: t.frequency,
                orderIndex: idx,
              })),
              requirements: vals.requirements.map((req, idx) => ({
                type: req.type,
                description: req.description || "",
                isRequired:
                  typeof req.isRequired === "string"
                    ? req.isRequired === "true"
                    : Boolean(req.isRequired),
                orderIndex: idx,
              })),
            });
          }}
        >
          <Save className="mr-1 h-4 w-4" />
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກແບບຮ່າງ"}
        </Button>
        <Button type="submit" isLoading={submitting} size="lg">
          ບັນທຶກ JD
        </Button>
      </div>
    </FormRoot>
  );
});

JDForm.displayName = "JDForm";
